import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Quote } from "lucide-react";
import { api } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { Stars } from "@/components/Stars";
import { REVIEW_BTN } from "@/components/ShowReviews";

const Card = ({ t, index }) => {
  const [open, setOpen] = useState(false);
  const long = t.text.length > 220;
  return (
    <figure className="relative flex flex-col rounded-3xl border border-white/10 bg-white/[0.04] p-6 testimonial-card" style={{ animationDelay: `${index * 90}ms` }} data-testid="testimonial-card">
      <Quote size={34} className="absolute -top-3 left-5 text-amber-400/80 fill-amber-400/80 rotate-180" aria-hidden="true" />
      <Stars value={t.rating} size={17} testId="testimonial-stars" />
      <blockquote className="mt-4 flex-1 text-[15px] leading-relaxed text-slate-200" data-testid="testimonial-text">
        {long && !open ? `${t.text.slice(0, 220).trimEnd()}… ` : t.text}
        {long && <button type="button" onClick={() => setOpen((o) => !o)} className="text-sky-300 text-sm font-semibold hover:underline" data-testid="testimonial-read-more">{open ? "Show less" : "Read more"}</button>}
      </blockquote>
      <figcaption className="mt-5 font-display text-lg font-black tracking-tight" data-testid="testimonial-name">— {t.name}{t.show_slug && <Link to={`/shows/${t.show_slug}`} className="block mt-0.5 font-sans text-xs font-semibold text-sky-300 hover:underline" data-testid="testimonial-show">on {t.show_name}</Link>}</figcaption>
    </figure>
  );
};

const JsonLd = ({ data }) => (
  <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(data).replace(/</g, "\\u003c") }} />
);

export const Testimonials = () => {
  const { homepage } = useSettings();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/testimonials", { params: { limit: 4 } }).then((r) => setData(r.data)).catch(() => setData({ items: [], aggregate: { count: 0 } })); }, []);
  if (homepage.reviews_enabled === false || !data || !data.items.length) return null;
  const ld = {
    "@context": "https://schema.org", "@type": "Organization", name: "Underdog Podcasts", url: window.location.origin,
    aggregateRating: { "@type": "AggregateRating", ratingValue: data.aggregate.average, bestRating: 5, worstRating: 0.5, ratingCount: data.aggregate.count },
    review: data.items.map((t) => ({ "@type": "Review", author: { "@type": "Person", name: t.name }, reviewRating: { "@type": "Rating", ratingValue: t.rating, bestRating: 5 }, reviewBody: t.text, datePublished: t.created_at.slice(0, 10) })),
  };
  return (
    <section id="reviews" className="max-w-[1400px] mx-auto px-5 sm:px-8 pb-20" data-testid="testimonials-section">
      <JsonLd data={ld} />
      <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
        <div>
          {homepage.reviews_eyebrow && <p className="eyebrow mb-2">{homepage.reviews_eyebrow}</p>}
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight" data-testid="testimonials-title">{homepage.reviews_title || "What listeners are saying"}</h2>
        </div>
        <Link to="/review" className={REVIEW_BTN} data-testid="testimonials-leave-review">{homepage.reviews_button || "Leave a review"}</Link>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="testimonials-grid">
        {data.items.map((t, i) => <Card key={t.id} t={t} index={i} />)}
      </div>
    </section>
  );
};
